import { ChartLayoutPicker } from '../chart/ChartLayoutPicker'
import type { ChartGridLayout } from '../TickerPriceChart'

export type InstrumentView = 'overview' | 'charts'

interface Props {
  view: InstrumentView
  onSelectView: (next: InstrumentView) => void
  /**
   * Page-level chart grid layout. When provided alongside onLayoutChange, the
   * layout picker is shown at the right of the tab row while the Charts view is
   * active — the layout affects the whole page, not any individual chart.
   */
  layout?: ChartGridLayout
  onLayoutChange?: (next: ChartGridLayout) => void
}

/**
 * Top-level view switch for the instrument page. Same underline tab style as
 * the Shares/Options tabs in the trade panel, aligned top-left.
 */
export function InstrumentHeader({ view, onSelectView, layout, onLayoutChange }: Props) {
  return (
    <header className="shrink-0 sticky top-0 z-30 flex items-center border-b border-zinc-800 bg-zinc-950/95 backdrop-blur-md px-2 sm:px-4">
      <TabButton active={view === 'overview'} onClick={() => onSelectView('overview')}>
        Overview
      </TabButton>
      <TabButton active={view === 'charts'} onClick={() => onSelectView('charts')}>
        Charts
      </TabButton>
      {view === 'charts' && layout && onLayoutChange && (
        <div className="ml-auto">
          <ChartLayoutPicker layout={layout} onLayoutChange={onLayoutChange} />
        </div>
      )}
    </header>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-3 sm:px-4 py-2 text-sm font-medium -mb-px border-b-2 transition ${
        active
          ? 'border-emerald-500 text-zinc-100'
          : 'border-transparent text-zinc-500 hover:text-zinc-300'
      }`}
    >
      {children}
    </button>
  )
}
